
import { Component } from 'vue-property-decorator';
import AddUserModal from '@/components/admin/AddUserModal.vue';
import CustomModal from '@/components/modal/CustomModal.vue';
import PasswordForm from '@/components/profile/PasswordForm.vue';
import ProfileForm from '@/components/profile/ProfileForm.vue';
import { ApiError } from '@/model/ApiError';
import { SignupFormData } from '@/model/Signup';
import {
    PasswordFormData,
    ProfileFormData,
    ProfileResponse,
    User,
} from '@/model/Profile';
import { Field } from '@/model/table/Field';
import Pageable from '@/model/Pageable';
import ConfirmationModal from '@/components/modal/ConfirmationModal.vue';
import { mapActions, mapGetters } from 'vuex';
import ToastVariant from '@/model/enums/toast/toast-variant';
import { mixins } from 'vue-class-component';
import ToastMixin from '@/mixins/ToastMixin';
import FormValidationMixin from '@/mixins/FormValidationMixin';
import FLAGS from '@/lib/flags';
import SortableTableHeader from '@/components/admin/SortableTableHeader.vue';
import {
    GetAllUserRequest,
    UserSortDirection,
} from '@/store/modules/admin-storage';
import PAGE_SIZE from '@/lib/pagination';

type UserSortField =
    | 'email'
    | 'fullName'
    | 'phone'
    | 'package'
    | 'chatbotTokenLimitMonthly'
    | 'chatbotTokensUsedMonthly'
    | 'chatbotEnabled';

@Component({
    components: {
        AddUserModal,
        CustomModal,
        PasswordForm,
        ProfileForm,
        ConfirmationModal,
        SortableTableHeader,
    },
    methods: {
        ...mapActions({
            signup: 'adminStorage/signup',
            getAllUser: 'adminStorage/getAllUser',
            deactivateUserById: 'adminStorage/deactivateUserById',
            reactivateUserById: 'adminStorage/reactivateUserById',
            deleteUserById: 'adminStorage/deleteUserById',
            updateAdminProfile: 'adminStorage/updateAdminProfile',
            updateUserChatbotEnabled: 'adminStorage/updateUserChatbotEnabled',
            updatePassword: 'adminStorage/updatePassword',
            fetchPackageHistory: 'adminStorage/fetchPackageHistory',
            fetchProfile: 'profileStorage/fetchProfile',
            fetchEvents: 'adminStorage/fetchEvents',
        }),
    },

    computed: {
        ...mapGetters({
            onClickError: 'adminStorage/getError',
            passwordError: 'adminStorage/getPasswordError',
            users: 'adminStorage/getUsers',
            loading: 'adminStorage/getLoading',
            historyLoading: 'adminStorage/getHistoryLoading',
            profile: 'profileStorage/getProfile',
        }),
    },
})
export default class AdminTable extends mixins(
    ToastMixin,
    FormValidationMixin,
) {
    updateLoading = false;

    addUserLoading = false;

    readonly isChatbotQuotaEnabled = FLAGS.chatbotQuota;

    users!: Pageable<User>;

    onClickError!: ApiError | null;

    profile!: ProfileResponse;

    signup!: (formData: SignupFormData) => Promise<void>;

    getAllUser!: (request: GetAllUserRequest) => Promise<void>;

    deactivateUserById!: (id: number) => Promise<void>;

    reactivateUserById!: (id: number) => Promise<void>;

    deleteUserById!: (id: number) => Promise<void>;

    fetchEvents!: (page: number) => Promise<void>;

    sortField: UserSortField | null = null;

    sortDirection: UserSortDirection | null = null;

    readonly pageSize = PAGE_SIZE;

    updateProfileFormResetKeys: Record<number, number> = {};

    chatbotToggleLoadingByUserId: Record<number, boolean> = {};

    get getTableFields(): Field[] {
        const fields = [
            {
                key: 'email',
                label: this.$i18n.t('admin.field.email').toString(),
            },
            {
                key: 'fullName',
                label: this.$i18n.t('admin.field.full-name').toString(),
            },
            {
                key: 'phone',
                label: this.$i18n.t('admin.field.phone').toString(),
            },
            {
                key: 'package',
                label: this.$i18n.t('admin.field.package').toString(),
            },
        ];

        if (this.isChatbotQuotaEnabled) {
            fields.push(
                {
                    key: 'chatbotTokenLimitMonthly',
                    label: this.$i18n
                        .t('admin.field.chatbot-token-limit')
                        .toString(),
                },
                {
                    key: 'chatbotTokensUsedMonthly',
                    label: this.$i18n
                        .t('admin.field.chatbot-tokens-used')
                        .toString(),
                },
            );
        }

        fields.push({
            key: 'actions',
            label: this.$i18n.t('admin.field.actions').toString(),
        });

        return fields;
    }

    get getHistoryFields(): Field[] {
        return [
            {
                key: 'packageType',
                label: this.$i18n.t('admin.field.packageType').toString(),
            },
            {
                key: 'startedAt',
                label: this.$i18n.t('admin.field.startedAt').toString(),
            },
            {
                key: 'endedAt',
                label: this.$i18n.t('admin.field.endedAt').toString(),
            },
        ];
    }

    getDisabledState(role: string, email: string): boolean {
        return role === 'ADMIN' && this.profile?.email === email;
    }

    canDeactivateUser(user: User): boolean {
        return user.active && !this.getDisabledState(user.role, user.email);
    }

    isUserChatbotEnabled(user: User): boolean {
        return user.chatbotEnabled !== false;
    }

    isChatbotToggleLoading(userId: number): boolean {
        return this.chatbotToggleLoadingByUserId[userId] === true;
    }

    setChatbotToggleLoading(userId: number, loading: boolean): void {
        this.$set(this.chatbotToggleLoadingByUserId, userId, loading);
    }

    formatPackageHistoryEndDate(
        endedAt: string | Date | null | undefined,
    ): string {
        return endedAt
            ? new Date(endedAt).toLocaleString()
            : this.$t('admin.field.active').toString();
    }

    close(): void {
        this.$bvModal.hide('add-user-modal');
    }

    updateAdminProfile!: ({
        formData,
        id,
    }: {
        formData: ProfileFormData;
        id: number;
    }) => Promise<User>;

    updateUserChatbotEnabled!: ({
        user,
        chatbotEnabled,
    }: {
        user: User;
        chatbotEnabled: boolean;
    }) => Promise<User>;

    updatePassword!: ({
        formData,
        id,
    }: {
        formData: PasswordFormData;
        id: number;
    }) => Promise<void>;

    fetchPackageHistory!: (userId: number) => Promise<void>;

    fetchProfile!: () => Promise<ProfileResponse>;

    beforeMount(): void {
        this.$store.commit('adminStorage/setError', null);
        this.loadUsers(0);
        this.fetchProfile();
    }

    loadUsers(page: number): Promise<void> {
        const request: GetAllUserRequest = { page };

        if (this.sortField && this.sortDirection) {
            request.sortField = this.sortField;
            request.sortDirection = this.sortDirection;
        }

        return this.getAllUser(request).catch(() => {
            if (this.onClickError) {
                this.showToast(
                    this.$t(this.onClickError.key).toString(),
                    this.$t('customer-project.error-title').toString(),
                    ToastVariant.DANGER,
                );
            }
        });
    }

    changeSort(field: UserSortField): void {
        if (this.sortField !== field) {
            this.sortField = field;
            this.sortDirection = 'asc';
        } else if (this.sortDirection === 'asc') {
            this.sortDirection = 'desc';
        } else if (this.sortDirection === 'desc') {
            this.sortField = null;
            this.sortDirection = null;
        } else {
            this.sortDirection = 'asc';
        }

        this.loadUsers(0);
    }

    getSortDirection(field: UserSortField): UserSortDirection | null {
        return this.sortField === field ? this.sortDirection : null;
    }

    softDeleteOnClick(id: number): void {
        this.updateLoading = true;
        this.deactivateUserById(id)
            .then(() => {
                this.showToast(
                    this.$t('admin.deactivate-success').toString(),
                    this.$t('success-title').toString(),
                    ToastVariant.SUCCESS,
                );
                this.$bvModal.hide(`deactivate-modal${id}`);
            })
            .catch(() => {
                if (this.onClickError) {
                    this.showToast(
                        this.onClickError.toString(),
                        this.$t('customer-project.error-title').toString(),
                        ToastVariant.DANGER,
                    );
                }
            })
            .finally(() => {
                this.updateLoading = false;
            });
    }

    reactivateOnClick(id: number): void {
        this.updateLoading = true;
        this.reactivateUserById(id)
            .then(() => {
                this.showToast(
                    this.$t('admin.reactivate-success').toString(),
                    this.$t('success-title').toString(),
                    ToastVariant.SUCCESS,
                );
                this.$bvModal.hide(`reactivate-modal${id}`);
            })
            .catch(() => {
                if (this.onClickError) {
                    this.showToast(
                        this.onClickError.toString(),
                        this.$t('customer-project.error-title').toString(),
                        ToastVariant.DANGER,
                    );
                }
            })
            .finally(() => {
                this.updateLoading = false;
            });
    }

    hardDeleteOnClick(id: number): void {
        this.updateLoading = true;
        this.deleteUserById(id)
            .then(() => {
                this.showToast(
                    this.$t('customer-project.success').toString(),
                    this.$t('success-title').toString(),
                    ToastVariant.SUCCESS,
                );
                this.$bvModal.hide(`delete-modal${id}`);
                this.fetchEvents(0);
                this.loadUsers(0);
            })
            .catch(() => {
                if (this.onClickError) {
                    this.showToast(
                        this.onClickError.toString(),
                        this.$t('customer-project.error-title').toString(),
                        ToastVariant.DANGER,
                    );
                }
            })
            .finally(() => {
                this.updateLoading = false;
            });
    }

    onToggleChatbot(user: User, chatbotEnabled: boolean): void {
        const previousChatbotEnabled = this.isUserChatbotEnabled(user);

        if (previousChatbotEnabled === chatbotEnabled) {
            return;
        }

        this.setChatbotToggleLoading(user.id, true);
        this.updateUserChatbotEnabled({ user, chatbotEnabled })
            .then(() => {
                this.showToast(
                    this.$t('profile.notification.successful').toString(),
                    this.$t('profile.notification.title').toString(),
                    ToastVariant.SUCCESS,
                );
            })
            .catch(() => {
                this.$set(user, 'chatbotEnabled', previousChatbotEnabled);
                if (this.onClickError) {
                    this.showToast(
                        this.$t(this.onClickError.key.toString()).toString(),
                        this.$t('customer-project.error-title').toString(),
                        ToastVariant.DANGER,
                    );
                }
            })
            .finally(() => {
                this.setChatbotToggleLoading(user.id, false);
            });
    }

    onSubmitProfile(formData: ProfileFormData, id: number): void {
        this.updateLoading = true;
        this.updateAdminProfile({ formData, id })
            .then(() => {
                this.showToast(
                    this.$t('profile.notification.successful').toString(),
                    this.$t('profile.notification.title').toString(),
                    ToastVariant.SUCCESS,
                );
                this.$bvModal.hide(`update-user-modal${id}`);
                this.loadUsers(this.users.currentPage);
            })
            .finally(() => {
                this.updateLoading = false;
            });
    }

    onSubmitPassword(formData: PasswordFormData, id: number): void {
        this.updateLoading = true;
        this.updatePassword({ formData, id })
            .then(() => {
                this.showToast(
                    this.$t('password.notification.successful').toString(),
                    this.$t('password.notification.title').toString(),
                    ToastVariant.SUCCESS,
                );
                this.loadUsers(this.users.currentPage);
            })
            .finally(() => {
                this.updateLoading = false;
            });
    }

    onSubmit(formData: SignupFormData): void {
        this.addUserLoading = true;
        this.signup({
            ...formData,
            acceptedTerms: true,
            acceptedPrivacyPolicy: true,
        })
            .then(() => {
                this.showToast(
                    this.$t('signup.notification.successful').toString(),
                    this.$t('signup.notification.title').toString(),
                    ToastVariant.SUCCESS,
                );
                this.$bvModal.hide('add-user-modal');
                this.loadUsers(0);
            })
            .catch(() => {
                if (this.onClickError) {
                    this.showToast(
                        this.$t(this.onClickError.key.toString()).toString(),
                        this.$t('customer-project.error-title').toString(),
                        ToastVariant.DANGER,
                    );
                }
            })
            .finally(() => {
                this.addUserLoading = false;
            });
    }

    onHidden(): void {
        this.$store.commit('adminStorage/setError', null);
    }

    getUpdateProfileFormKey(id: number): string {
        return `profile-form-${id}-${this.updateProfileFormResetKeys[id] ?? 0}`;
    }

    resetUpdateProfileForm(id: number): void {
        this.$set(
            this.updateProfileFormResetKeys,
            id,
            (this.updateProfileFormResetKeys[id] ?? 0) + 1,
        );
    }
}
